/**
 * Weekly cash-flow projection (W-29, basic version): opening cash in base
 * currency + open receivables by due date − open payables by due date.
 * Phase 7 adds expected Amazon payouts and planned purchase orders.
 * Items without a due date or already overdue fall into the first week.
 */
import { addDays, daysBetween, toIsoDate, type DateInput } from './format';
import { D, sum, type Dec, type DecimalInput } from './money';

export interface CashflowItem {
  dueDate: string | null;
  /** base-currency amount; + inflow (receivable) / − outflow (payable) */
  amountBase: DecimalInput;
}

export interface CashflowWeek<I extends CashflowItem = CashflowItem> {
  start: string;
  inflow: Dec;
  outflow: Dec;
  net: Dec;
  closing: Dec;
  /** the items that fell into this week — what the "neden?" drawer lists */
  items: I[];
}

/** Monday of the week containing `d` (week starts Monday). */
export function weekStart(d: DateInput): string {
  const iso = toIsoDate(d);
  const [y, m, day] = iso.split('-').map(Number) as [number, number, number];
  const wd = (new Date(Date.UTC(y, m - 1, day)).getUTCDay() + 6) % 7;
  return addDays(iso, -wd);
}

export function projectCashflow<I extends CashflowItem>(opening: DecimalInput, items: I[], weeks = 12, today: DateInput = new Date()): CashflowWeek<I>[] {
  const first = weekStart(today);
  const out: CashflowWeek<I>[] = Array.from({ length: weeks }, (_, i) => ({
    start: addDays(first, i * 7),
    inflow: D(0),
    outflow: D(0),
    net: D(0),
    closing: D(0),
    items: [],
  }));
  for (const it of items) {
    const idx = it.dueDate ? Math.max(0, Math.floor(daysBetween(first, it.dueDate) / 7)) : 0;
    if (idx >= weeks) continue;
    const a = D(it.amountBase);
    const w = out[idx]!;
    if (a.isNegative()) w.outflow = w.outflow.plus(a.abs());
    else w.inflow = w.inflow.plus(a);
    w.items.push(it);
  }
  let running = D(opening);
  for (const w of out) {
    w.net = w.inflow.minus(w.outflow);
    running = running.plus(w.net);
    w.closing = running;
  }
  return out;
}

export function lowestPoint<I extends CashflowItem>(weeks: CashflowWeek<I>[]): CashflowWeek<I> | null {
  return weeks.reduce<CashflowWeek<I> | null>((min, w) => (!min || w.closing.lessThan(min.closing) ? w : min), null);
}

/** What the horizon adds up to — the "Projeksiyon bileşenleri" card. */
export interface CashflowTotals {
  /** açık ticari alacaklar falling inside the horizon */
  inflow: Dec;
  /** açık ticari borçlar falling inside the horizon */
  outflow: Dec;
  /** inflow − outflow, i.e. what the horizon adds to the opening cash */
  net: Dec;
  /** cash at the end of the horizon (opening + net) */
  closing: Dec;
}

export function cashflowTotals<I extends CashflowItem>(opening: DecimalInput, weeks: CashflowWeek<I>[]): CashflowTotals {
  const inflow = sum(weeks.map((w) => w.inflow));
  const outflow = sum(weeks.map((w) => w.outflow));
  const net = inflow.minus(outflow);
  return { inflow, outflow, net, closing: D(opening).plus(net) };
}

/** A cash/bank account the way v_account_balances returns it. */
export interface CashAccountLike {
  type: string | null;
  is_closed?: boolean | null;
  /** TRY carrying value of the balance (v_account_balances.balance_base) */
  balance_base: DecimalInput;
}

/**
 * Opening cash for the projection: every account at its TRY carrying value, so
 * a USD or EUR bank account counts too — the open items fed into the projection
 * are converted to TRY as well (v_open_items.remaining_base) and the two sides
 * have to be in the same currency. Partner current accounts are not company
 * cash, and a closed account cannot be spent either.
 */
export function openingCashBase(accounts: CashAccountLike[]): Dec {
  return sum(accounts.filter((a) => a.type !== 'partner' && !a.is_closed).map((a) => a.balance_base));
}

/** An open invoice the way v_open_items returns it. */
export interface OpenItemLike {
  invoice_id?: string | null;
  type: string | null;
  no?: string | null;
  due_date: string | null;
  contact_id?: string | null;
  /** TRY carrying value of what is still open (v_open_items.remaining_base) */
  remaining_base: DecimalInput;
}

export interface OpenItemCashflowItem extends CashflowItem {
  invoiceId: string | null;
  no: string | null;
  contactId: string | null;
  /** a sale is money coming in, everything else is money going out */
  isInflow: boolean;
  /** what is still open, always positive */
  remainingBase: Dec;
}

/** Open invoices as projection inputs: sale → tahsilat (+), purchase → ödeme (−). */
export function openItemsToCashflow(rows: OpenItemLike[]): OpenItemCashflowItem[] {
  return rows.map((o) => {
    const isInflow = o.type === 'sale';
    const remainingBase = D(o.remaining_base);
    return {
      dueDate: o.due_date,
      amountBase: isInflow ? remainingBase : remainingBase.negated(),
      invoiceId: o.invoice_id ?? null,
      no: o.no ?? null,
      contactId: o.contact_id ?? null,
      isInflow,
      remainingBase,
    };
  });
}
